// Ylläpidon "Lisää reitti GPX:stä" -lomakkeen logiikka (components/RouteImportSheet.jsx).
// Palvelin (backend/app/route_admin.py) muodostaa linjan ja laskee pisteet; tämä tiedosto huolehtii siitä,
// mitä lomake näyttää, mitä se hyväksyy ja mitä se lähettää. Puhtaita funktioita, jotta ne on helppo testata.

import { routeLines, routeVertices } from "./routeGeometry.js";

export const DIFFICULTY_OPTIONS = [
  { id: "none", label: "Ei määritelty" },
  { id: "easy", label: "Helppo" },
  { id: "medium", label: "Keskivaikea" },
  { id: "hard", label: "Vaativa" },
];

const MAX_POINTS = 5000;
const MAX_KM = 500;

/** Desimaaliluku, joka voi olla pilkulla kirjoitettu. Tyhjä merkkijono = ei arvoa (null); virheellinen = NaN. */
export function parseNumber(text) {
  const t = String(text ?? "").trim().replace(",", ".");
  if (!t) return null;
  return /^\d+(\.\d+)?$/.test(t) ? Number(t) : Number.NaN;
}

/** Pisteet, jotka reitti saisi: oma arvo, jos annettu, muuten palvelimen laskema vaativuuden mukaan. */
export function pointsFor(preview, difficulty, overrideText) {
  const own = parseNumber(overrideText);
  if (own !== null && Number.isInteger(own) && own >= 0 && own <= MAX_POINTS) return own;
  return preview?.points_by_difficulty?.[difficulty || "none"] ?? null;
}

/** Vaativuusvalinnan teksti pistemäärineen, esim. "Helppo (400 p)". */
export function difficultyLabel(option, preview) {
  const pts = preview?.points_by_difficulty?.[option.id];
  return pts == null ? option.label : `${option.label} (${pts} p)`;
}

/** Varoitus, jos osa GPX:stä jäi pois (liian kaukana liitettäväksi). */
export function dropWarning(preview) {
  if (!preview?.dropped_parts) return null;
  const n = preview.dropped_parts;
  return `${n} ${n === 1 ? "osa" : "osaa"} (${String(preview.dropped_km).replace(".", ",")} km) jäi pois, koska ${n === 1 ? "se on" : "ne ovat"} yli 120 m:n päässä muusta reitistä. Tarkista kuva.`;
}

/**
 * Lomakkeen virheet suomeksi. mode "new" = uusi reitti, "replace" = vain olemassa olevan reitin linja.
 * Tyhjä lista = voi tallentaa.
 */
export function formErrors(form, mode, preview) {
  const errors = [];
  if (!preview) return ["Valitse ensin GPX-tiedosto."];
  if (mode === "replace") {
    if (!form.replaceId) errors.push("Valitse reitti, jonka linja korvataan.");
    return errors;
  }
  if (!form.name?.trim()) errors.push("Anna reitille nimi.");
  if (!form.municipality?.trim()) errors.push("Anna kunta.");
  const pts = parseNumber(form.points);
  if (Number.isNaN(pts) || (pts !== null && (!Number.isInteger(pts) || pts < 0 || pts > MAX_POINTS))) {
    errors.push(`Pisteet ovat kokonaisluku 0–${MAX_POINTS}.`);
  }
  const km = parseNumber(form.length);
  if (Number.isNaN(km) || (km !== null && (km <= 0 || km > MAX_KM))) errors.push(`Pituus on luku väliltä 0–${MAX_KM} km.`);
  return errors;
}

/** Palvelimelle lähetettävä runko. Tyhjät kentät jätetään pois, jotta palvelin käyttää oletusta. */
export function savePayload(file, form, mode) {
  const base = { filename: file.name, content_b64: file.b64 };
  if (mode === "replace") return { ...base, replace_route_id: form.replaceId };
  const body = { ...base, name: form.name.trim(), municipality: form.municipality.trim() };
  const optional = {
    area: form.area?.trim() || null,
    description: form.description?.trim() || null,
    note: form.note?.trim() || null,
    difficulty: form.difficulty && form.difficulty !== "none" ? form.difficulty : null,
    points: parseNumber(form.points),
    length_km: parseNumber(form.length),
  };
  for (const [k, v] of Object.entries(optional)) if (v !== null) body[k] = v;
  return body;
}

/** Tallennuksen jälkeinen viesti { what, sub } ilmoitukseen. */
export function savedMessage(result) {
  if (result.mode === "replaced") return { what: "Reitin linja korvattu", sub: `${String(result.length_km).replace(".", ",")} km, ${result.points} pistettä linjassa.` };
  return { what: "Reitti lisätty", sub: `${result.points ?? "?"} pistettä, ${String(result.length_km).replace(".", ",")} km.` };
}

/** Kunnat valintalistaan: reiteillä jo olevat, aakkosjärjestyksessä. */
export function municipalities(content) {
  return [...new Set((content?.routes ?? []).map((r) => r.municipality).filter(Boolean))].sort((a, b) => a.localeCompare(b, "fi"));
}

/** Reitit, joiden linjan voi korvata: linjattomat ensin, sitten nimen mukaan. */
export function replaceCandidates(content) {
  return [...(content?.routes ?? [])]
    .map((r) => ({ id: r.id, name: r.name, noLine: !r.geometry, label: `${r.name}${r.geometry ? "" : " (ei linjaa)"}` }))
    .sort((a, b) => Number(b.noLine) - Number(a.noLine) || a.name.localeCompare(b.name, "fi"));
}

/** SVG-polku linjan pienoiskuvalle: sovittaa linjan laatikkoon w × h säilyttäen mittasuhteet. */
export function sketchPath(geometry, w = 280, h = 140, pad = 8) {
  const lines = routeLines(geometry);
  geometry = routeVertices(geometry);
  if (!geometry.length) return "";
  const lat0 = geometry.reduce((s, p) => s + p[0], 0) / geometry.length;
  const kx = Math.cos((lat0 * Math.PI) / 180); // pituusasteen kutistuma, jotta kuva ei veny
  const xs = geometry.map((p) => p[1] * kx);
  const ys = geometry.map((p) => -p[0]);
  const minX = Math.min(...xs), maxX = Math.max(...xs), minY = Math.min(...ys), maxY = Math.max(...ys);
  const scale = Math.min((w - 2 * pad) / (maxX - minX || 1e-9), (h - 2 * pad) / (maxY - minY || 1e-9));
  const offX = (w - (maxX - minX) * scale) / 2;
  const offY = (h - (maxY - minY) * scale) / 2;
  return lines.map((line) => line.map((p, i) =>
    `${i === 0 ? "M" : "L"}${(offX + (p[1] * kx - minX) * scale).toFixed(1)} ${(offY + (-p[0] - minY) * scale).toFixed(1)}`
  ).join(" ")).join(" ");
}

